import './styles.scss'

import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from '@AppState/redux-store'
import { Button } from '@Components/common/button'
import { BUTTON_TYPE } from '@Components/common/types'
import { dynamicTemplate } from '@Util/dynamic-template'
import { Utils } from '@Util/utils'

import { DismissibleAnimationModal } from '../animation-modal/dismissible-animation-modal'

import { SoundController } from '@Animation/sound-controller'
import { GameController } from '@Animation/game-control'

const CLASS_NAME = 'gby-k2g-animation-payout-modal'

export type AnimationPayoutModalProps = {
  totalWinnings: number
  numberOfDraws: number
  onButtonClick: () => void
  onClose: () => void
}

let smallWinSound: Phaser.Sound.BaseSound | null = null
let bigWinSound: Phaser.Sound.BaseSound | null = null

export const AnimationPayoutModal = (props: AnimationPayoutModalProps) => {
  const modalConfig = useSelector(
    (state: RootState) => state.hostConfig.config?.content.payoutModals ?? [],
  )

  const scene = GameController.getDrawAnimationScene()
  const sounds = GameController.getSounds()

  if(scene && sounds){
    if(smallWinSound == null){
      smallWinSound = scene.sound.add(sounds?.SMALL_WIN)
    }

    if(bigWinSound == null){
      bigWinSound = scene.sound.add(sounds?.BIG_WIN)
    }
  }

  const getModalTemplate = useCallback(
    (winAmount: number) => {
      const possibleTemplates = modalConfig.filter(
        (res) =>
          winAmount >= res.minThreshold && (res.maxThreshold ? winAmount < res.maxThreshold : true),
      )
      const selection = Utils.getRandomElementFromArray(possibleTemplates)

      return {
        ...selection,
        winAmount,
      }
    },
    [modalConfig],
  )

  const bodyTemplate = useCallback(
    (templateStr: string) => {
      return dynamicTemplate(templateStr, {
        totalWinningsStr: Utils.convertCentsNumberToDollarString(props.totalWinnings),
        numberOfDraws: props.numberOfDraws,
      })
    },
    [props],
  )

  const modalTemplate = getModalTemplate(props.totalWinnings)
  
  useEffect(() => {
    if (bigWinSound != null && props.totalWinnings >= 10000) {
      SoundController.playSound ( bigWinSound )
    } else if (smallWinSound != null && props.totalWinnings > 1 && props.totalWinnings < 10000){
      SoundController.playSound ( smallWinSound )
    }
  }, [])

  return (
    <DismissibleAnimationModal
      canClose={true}
      title={`Ticket Payout Summary`}
      description={bodyTemplate(
        props.numberOfDraws > 1
          ? modalTemplate.multiDrawBodyTextTemplateStr
          : modalTemplate.singleDrawBodyTextTemplateStr,
      )}
      footer={
        <div className={`${CLASS_NAME}__button-container`}>
          <Button
            classPrefix={`${CLASS_NAME}__button`}
            text={'Check Another Ticket'}
            type={BUTTON_TYPE.SUBMIT}
            onClick={props.onButtonClick}
          />
        </div>
      }
      onClose={props.onClose}
      overlayZIndex={15}>
      <div className={`${CLASS_NAME}`}>
        <div className={`${CLASS_NAME}__title`}>
          <div className={`${CLASS_NAME}__title-label`}>{modalTemplate.title}</div>
          {props.totalWinnings > 0 ? (
            <div className={`${CLASS_NAME}__title-win-amount`}>
              {Utils.convertCentsNumberToDollarString(props.totalWinnings)}
            </div>
          ) : null}
        </div>

        <img
          className={`${CLASS_NAME}__image`}
          src={modalTemplate.imageUrl}
          alt={'Prize Modal Icon Image'}
        />

        <div
          className={`${CLASS_NAME}__text`}
          dangerouslySetInnerHTML={{
            __html: bodyTemplate(
              props.numberOfDraws > 1
                ? modalTemplate.multiDrawBodyTextTemplateStr
                : modalTemplate.singleDrawBodyTextTemplateStr,
            ),
          }}
        />
      </div>
    </DismissibleAnimationModal>
  )
}
